import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { SETTING, SIGNUP } from 'routes/routeConstant'
import { getStripePlan } from '../../redux/actions/myAccountAction'
import { membershipNameType } from '../../utils/constant'
import { formatMoney, getPlanAmount } from 'utils/currency'
import {
  WIN_BACK_PROMO,
  getWinBackPromoPricing,
  getWinBackPromoTimeLeft,
  isWinBackPromoActive,
} from '../../utils/winBackPromo'
import { captureOfferCoupon } from '../../utils/offerCoupon'
import './offer.css'

const pad = (n: number) => String(n).padStart(2, '0')

const BRAND_NAME = 'maayot'

/**
 * Public offer landing page. No account, no login, no gate.
 *
 * Exists because the Meta offer ad promises a price, and the only page that could show
 * that price lived behind a login. Sending paid traffic to a login screen to see a
 * discount is how you pay for a click and lose it.
 *
 * **Monthly only, and that is the point.** The campaign coupon carries
 * interval_scope=month, so the yearly plans are not discounted and showing them here
 * would advertise a saving that checkout would refuse.
 *
 * Buying still requires an account. Stripe cannot charge without a customer and a
 * customer needs a signup, so a signed-out visitor goes to /signup with the coupon
 * remembered. See `offerCoupon.ts` for how it survives the walk to checkout.
 *
 * **A signed-in learner goes straight to the plan page instead.** Sending them to
 * /signup would be worse than useless: `useOnboardingBootstrap` bounces an existing
 * account off that route to /stories, so they would click an offer and land on a story
 * list with no price and no explanation.
 *
 * When the window closes this page stops showing prices and points at signup instead,
 * rather than 404ing. An ad, an email or a screenshot outliving its campaign is normal;
 * a dead link is worse than a page that has moved on.
 */
const Offer = () => {
  const { t } = useTranslation('offers')
  const navigate = useNavigate()
  const [stripePlan, setStripePlan] = useState<any>(null)
  const [timeLeft, setTimeLeft] = useState(getWinBackPromoTimeLeft())
  const live = isWinBackPromoActive()

  useEffect(() => {
    captureOfferCoupon()
  }, [])

  useEffect(() => {
    getStripePlan().then((res: any) => {
      if (res?.monthly || res?.yearly) setStripePlan(res)
    })
  }, [])

  useEffect(() => {
    if (!live) return
    const timer = setInterval(() => setTimeLeft(getWinBackPromoTimeLeft()), 1000)
    return () => clearInterval(timer)
  }, [live])

  const cards = useMemo(() => {
    const monthly = stripePlan?.monthly || []
    return [membershipNameType.Standard, membershipNameType.Pro]
      .map((value) => {
        const plan = monthly.find((p: any) => p?.value === value)
        if (!plan) return null
        const list = getPlanAmount(plan)
        const pricing = live ? getWinBackPromoPricing(list) : null
        return {
          value,
          currency: plan.currency,
          regular: list,
          now: pricing ? pricing.promoMonthly : list,
          saving: pricing ? pricing.regularMonthly - pricing.promoMonthly : 0,
          discounted: Boolean(pricing),
        }
      })
      .filter(Boolean) as Array<{
      value: string
      currency: string
      regular: number
      now: number
      saving: number
      discounted: boolean
    }>
  }, [stripePlan, live])

  // Flag whichever card saves the most, computed rather than assumed. The same
  // percentage off a higher price is a bigger saving, so this lands on Pro at today's
  // prices, but it will not lie if the prices ever change.
  const bestSaving = useMemo(
    () => cards.reduce((best, c) => (c.saving > best ? c.saving : best), 0),
    [cards],
  )

  // Read once on mount rather than per render: this only decides where the button goes.
  const isSignedIn = useMemo(() => {
    try {
      return Boolean(localStorage.getItem('authToken'))
    } catch {
      return false
    }
  }, [])

  const start = () =>
    isSignedIn
      ? navigate({ pathname: SETTING, search: window.location.search, hash: 'plan' })
      : navigate({ pathname: SIGNUP, search: window.location.search })

  return (
    <div className='offer-page'>
      <div className='offer-inner'>
        <img className='offer-mark' src='/images/maayot-text-logo.png' alt={BRAND_NAME} />

        {live ? (
          <>
            <h1 className='offer-title'>
              <Trans t={t} i18nKey='offer.live.title' components={[<span key='0' />]} />
            </h1>
            <p className='offer-sub'>{t('offer.live.subtitle')}</p>

            <div className='offer-countdown'>
              {t('offer.live.countdown', {
                days: pad(timeLeft.days),
                hours: pad(timeLeft.hours),
                minutes: pad(timeLeft.minutes),
                seconds: pad(timeLeft.seconds),
              })}
            </div>

            <div className='offer-cards'>
              {cards.length === 0 ? (
                <div className='offer-loading'>{t('offer.live.loadingPrices')}</div>
              ) : (
                cards.map((c) => (
                  <div
                    key={c.value}
                    className={`offer-card${
                      c.discounted && c.saving === bestSaving ? ' offer-card--best' : ''
                    }`}
                  >
                    {c.discounted && c.saving > 0 && c.saving === bestSaving && (
                      <span className='offer-card-flag'>
                        {t('offer.live.saveFlag', { amount: formatMoney(c.saving, c.currency) })}
                      </span>
                    )}
                    <h2>{c.value}</h2>
                    <div className='offer-price'>
                      {c.discounted && (
                        <span className='offer-price-old'>{formatMoney(c.regular, c.currency)}</span>
                      )}
                      <span className='offer-price-now'>{formatMoney(c.now, c.currency)}</span>
                      <span className='offer-price-period'>{t('offer.live.perMonth')}</span>
                    </div>
                    {c.discounted && (
                      <p className='offer-price-note'>
                        {t('offer.live.priceNote', {
                          months: WIN_BACK_PROMO.durationMonths,
                          price: formatMoney(c.regular, c.currency),
                        })}
                      </p>
                    )}
                    {/* Pro leads on what it adds rather than repeating the shared
                        features. Everything listed here is Pro-only, checked against
                        the live plan response: Standard keeps the last 5 studied lessons,
                        Pro keeps 30, and only Pro carries the corrections. */}
                    {c.value === membershipNameType.Pro ? (
                      <>
                        <p className='offer-features-lead'>{t('offer.live.proLead')}</p>
                        <ul className='offer-features'>
                          <li>{t('offer.live.proFeatures.corrections')}</li>
                          <li>{t('offer.live.proFeatures.history')}</li>
                        </ul>
                      </>
                    ) : (
                      <ul className='offer-features'>
                        <li>{t('offer.live.standardFeatures.dailyLesson')}</li>
                        <li>{t('offer.live.standardFeatures.audio')}</li>
                        <li>{t('offer.live.standardFeatures.practice')}</li>
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>

            <button className='offer-cta' onClick={start} type='button'>
              {t('offer.live.cta')}
            </button>
            <p className='offer-fineprint'>{t('offer.live.finePrint')}</p>
            <p className='offer-fineprint'>{t('common:teacherLanguageNotice')}</p>
          </>
        ) : (
          <>
            <h1 className='offer-title'>
              <Trans t={t} i18nKey='offer.closed.title' components={[<span key='0' />]} />
            </h1>
            <p className='offer-sub'>{t('offer.closed.subtitle')}</p>
            <button className='offer-cta' onClick={start} type='button'>
              {t('offer.closed.cta')}
            </button>
            <p className='offer-fineprint'>{t('common:teacherLanguageNotice')}</p>
          </>
        )}
      </div>
    </div>
  )
}

export default Offer
